/**
 * The words a workflow is written in.
 *
 * A workflow is one sentence: when this happens, and these things are true, do
 * these things. Everything in this file is part of that sentence, and every
 * list here is the single source of truth the API, the engine and the client
 * all read, so none of them can offer a word the others do not understand.
 */

export const workflowStatuses = ["active", "paused", "system_paused"] as const

export type WorkflowStatus = (typeof workflowStatuses)[number]

/**
 * `system_paused` is separate from `paused` on purpose: the page has to be able
 * to say we turned a rule off, and why, rather than showing the same grey
 * chip a customer's own pause gets.
 */
export const workflowRunOutcomes = [
  "matched",
  "skipped",
  "failed",
  "partial",
] as const

export type WorkflowRunOutcome = (typeof workflowRunOutcomes)[number]

/** Runs that did something, as opposed to runs we considered and skipped. */
export const workflowActedOutcomes = [
  "matched",
  "failed",
  "partial",
] as const satisfies readonly WorkflowRunOutcome[]
const workflowActedOutcomeSet = new Set<WorkflowRunOutcome>(
  workflowActedOutcomes
)

export function workflowRunActed(outcome: WorkflowRunOutcome): boolean {
  return workflowActedOutcomeSet.has(outcome)
}

export const workflowActionTypes = [
  "post_to_quickbooks",
  "email_copy",
  "send_to_webhook",
  "build_export",
] as const

export type WorkflowActionType = (typeof workflowActionTypes)[number]

export const workflowConditionFields = [
  "unresolved_flags",
  "flag_kind",
  "total_amount",
  "currency",
  "counterparty",
  "document_date",
  "reconciliation_status",
  "arrival_source",
  "page_count",
  "category",
] as const

export type WorkflowConditionField = (typeof workflowConditionFields)[number]

export const workflowComparisons = [
  "is_none",
  "is_any",
  "count_over",
  "includes",
  "does_not_include",
  "is_over",
  "is_under",
  "is_between",
  "is",
  "is_not",
  "contains",
  "does_not_contain",
  "in_last_days",
  "is_before",
  "is_after",
] as const

export type WorkflowComparison = (typeof workflowComparisons)[number]

/**
 * What kind of control the third position in a condition row is, and therefore
 * what shape its stored value takes. Declared per field and comparison pair,
 * because `total amount is between` needs two numbers where `is over` needs
 * one.
 */
export const workflowValueKinds = [
  "none",
  "count",
  "amount",
  "amount_range",
  "text",
  "currency_code",
  "days",
  "date",
  "flag_type",
  "reconciliation_status",
  "arrival_source",
  /**
   * A category id. Unlike every other fixed-list kind the choices are
   * workspace data, not a compile-time list, so the editor reads them from
   * the catalog endpoint rather than from workflowValueOptions.
   */
  "category",
] as const

export type WorkflowValueKind = (typeof workflowValueKinds)[number]

/**
 * How a document reached the workspace. Narrower than the intake `batch_source`
 * enum on purpose: a customer thinks in "I uploaded it", "it was forwarded" and
 * "our system sent it", and the legacy backfill source is none of those, so it
 * reads as unknown and every check against it fails rather than guessing.
 */
export const workflowArrivalSources = ["upload", "email", "api"] as const

export type WorkflowArrivalSource = (typeof workflowArrivalSources)[number]

/** The reconciliation verdict a bank statement extraction records. */
export const workflowReconciliationStatuses = [
  "balanced",
  "out_of_balance",
  "not_available",
] as const

export type WorkflowReconciliationStatus =
  (typeof workflowReconciliationStatuses)[number]

/** What a prerequisite check on an action is asking about. */
export const workflowPrerequisiteKeys = [
  "pro_plan",
  "paid_plan",
  "quickbooks_connected",
  "quickbooks_destination",
  "webhook_endpoint",
  "confirmed_recipients",
] as const

export type WorkflowPrerequisiteKey = (typeof workflowPrerequisiteKeys)[number]

/** Why we turned a rule off ourselves. */
export const workflowSystemPauseReasons = [
  "consecutive_failures",
  "auth_failure",
] as const

export type WorkflowSystemPauseReason =
  (typeof workflowSystemPauseReasons)[number]

/**
 * Consecutive failures before we pause a rule. There is no daily run ceiling by
 * decision, so this counter is the only backstop against a broken rule running
 * all month, which is why it is small.
 */
export const workflowAutoPauseFailureThreshold = 5

/** How many recent documents the dry run replays the checks against. */
export const workflowTestSampleSize = 25

/**
 * How long run history is kept, printed on the history panel itself rather than
 * buried in a help page. A year covers an accountant asking at year end why an
 * entry exists; a week covers the real question about skips, which is why
 * nothing happened the last time somebody closed a month.
 */
export const workflowRunRetentionDays = {
  acted: 365,
  skipped: 7,
} as const

/** Ceilings that keep one rule readable, and keep a saved rule bounded. */
export const workflowLimits = {
  nameMaxLength: 200,
  maxConditions: 10,
  maxActions: 6,
  maxEmailRecipients: 5,
  emailNoteMaxLength: 500,
  textValueMaxLength: 200,
  maxDaysWindow: 3650,
  /** What the money columns can hold, so a saved amount is always comparable. */
  maxAmount: 999_999_999_999.99,
  /** Flags and pages on one document. Well past anything real, and finite. */
  maxCount: 1_000_000,
} as const
